<script setup lang="ts">
export interface SaferGamblingQaContentProps {
    videoId?: string
    body: any
    ctas?: Array<{
        label: string
        url: string
        variant?: 'primary' | 'secondary'
    }>
    expandableTextBlocks?: Array<{
        title: string
        content: any
    }>
}

const props = withDefaults(defineProps<SaferGamblingQaContentProps>(), {
    ctas: () => [],
    expandableTextBlocks: () => []
})

const isVimeo = computed(() => /^\d+$/.test(props.videoId ?? ''))

const videoEmbedUrl = computed(() => {
    if (!props.videoId) return undefined
    return isVimeo.value
        ? `https://player.vimeo.com/video/${props.videoId}`
        : `https://www.youtube.com/embed/${props.videoId}`
})

const videoAllowAttr = computed(() => {
    return isVimeo.value
        ? 'autoplay; fullscreen; picture-in-picture'
        : 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
})

function openInNewWindow(url: string) {
    window.open(url, '_blank', 'noreferrer noopener')
}
</script>

<template>
    <div class="qa-content">
        <div
            v-if="props.videoId"
            class="qa-content__video"
        >
            <iframe
                :src="videoEmbedUrl"
                title="Video"
                frameborder="0"
                :allow="videoAllowAttr"
                allowfullscreen
            />
        </div>

        <ContentRenderer :value="props.body" />

        <SaferGamblingSubPanel
            v-for="block in props.expandableTextBlocks"
            :key="`expandable-block-${block.title}`"
            :title="block.title"
        >
            <p>{{ block.content }}</p>
        </SaferGamblingSubPanel>

        <UIButton
            v-for="(cta, index) in props.ctas"
            :key="`cta-${index}`"
            :class="['btn btn--block', `btn--${cta.variant || 'primary'}`]"
            :rounded="false"
            @click="openInNewWindow(cta.url)"
        >
            {{ cta.label }}
        </UIButton>
    </div>
</template>

<style scoped lang="scss">
.qa-content {
    text-align: left;

    &__video {
        margin-bottom: 20px;

        iframe {
            width: 100%;
            aspect-ratio: 16 / 9;
            border-radius: 8px;
        }
    }

    :deep(p) {
        margin-bottom: 1em;

        > b {
            display: block;
            transform: translateY(10px);
        }
    }

    :deep(ul),
    :deep(ol) {
        padding-left: 1.5em;
        margin-bottom: 1em;

        li {
            text-align: left;
        }
    }

    :deep(h2),
    :deep(h3),
    :deep(h4) {
        margin-top: 1.5em;
        margin-bottom: 0.5em;
        font-weight: $text-bold;
    }

    :deep(.btn) {
        height: 50px;
        align-items: center;
        margin-top: 15px;
    }
}
</style>
